import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Embroidery, chosen in the cart.
 *
 * One press adds a name to one piece. On a line of three, the press splits a
 * single towel onto its own line and leaves the other two alone — the shopper
 * presses again for a second name rather than being asked to understand what a
 * cart line is. That split is the whole reason this lives in the cart: on a
 * product page the quantity stepper and the lettering field contradict each
 * other, and "three towels, a name on two of them" has no honest answer there.
 *
 * The charge rides on its own cart line (the service product) carrying the
 * name, lettering and thread; the piece carries nothing but a shared `_emb_id`
 * linking the two. So a name never touches the catalogue — no extra variant,
 * and no second stock pool for one physical towel.
 */
class UchinoEmbroidery extends HTMLElement {
  /** @type {'add' | 'edit'} */
  #mode = 'add';

  /** @type {Record<string, string>} */
  #context = {};

  #busy = false;

  #sweeping = false;

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.nameInput = this.querySelector('[data-emb-name]');
    this.count = this.querySelector('[data-emb-count]');
    this.preview = this.querySelector('[data-emb-preview]');
    this.echo = this.querySelector('[data-emb-echo]');
    this.confirm = this.querySelector('[data-emb-confirm]');
    this.submit = this.querySelector('[data-emb-submit]');
    this.submitText = this.querySelector('[data-emb-submit-text]');
    this.pieceLabel = this.querySelector('[data-emb-piece]');
    this.threadName = this.querySelector('[data-emb-thread-name]');
    this.error = this.querySelector('[data-emb-error]');

    this.styles = [...this.querySelectorAll('[data-emb-style]')];
    this.threads = [...this.querySelectorAll('[data-emb-thread]')];

    this.max = Number(this.dataset.max) || 8;
    this.variantId = this.dataset.variantId;
    this.addLabel = this.submitText?.textContent ?? 'Add embroidery';

    this.#ensureThreadChecked();

    this.addEventListener('input', this.#onChange);
    this.addEventListener('change', this.#onChange);
    this.querySelector('[data-emb-close]')?.addEventListener('click', () => this.close());
    this.submit?.addEventListener('click', this.#onSubmit);

    // Clicking the backdrop closes it. A dialog covering the cart with no
    // visible way out reads as a trap.
    this.dialog?.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    });

    // Delegated, because the cart re-renders itself from the server after every
    // change: any button bound directly would be replaced out from under us.
    document.addEventListener('click', this.#onDocumentClick);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#sweepOrphans);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#onDocumentClick);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#sweepOrphans);
  }

  /**
   * Deleting the towel with the theme's own bin leaves its embroidery charge
   * behind: a ten dollar line for a piece that is no longer in the cart. The
   * shopper can see it and remove it, but they should not have to notice — so
   * the charge follows the piece out.
   */
  #sweepOrphans = async (event) => {
    if (this.#sweeping || this.#busy) return;
    this.#sweeping = true;

    try {
      // The theme announces the change before it has made it. Reading the cart
      // on the event alone finds the piece still there and nothing to sweep.
      await event?.promise?.catch(() => {});

      const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      const cart = await response.json();

      const attached = new Set(cart.items.map((item) => item.properties?._emb_id).filter(Boolean));
      const orphans = cart.items.filter(
        (item) => item.properties?._emb_for && !attached.has(item.properties._emb_for)
      );
      if (!orphans.length) return;

      for (const orphan of orphans) {
        await this.#cart(Theme.routes.cart_change_url, { id: orphan.key, quantity: 0 });
      }
      await this.#refreshCart({ id: String(orphans[0].key), quantity: 0 });
    } catch (error) {
      console.warn('[uchino-embroidery] orphan sweep', error);
    } finally {
      this.#sweeping = false;
    }
  };

  /* ------------------------------------------------------------------ open */

  #onDocumentClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const add = target.closest?.('[data-uchino-emb-add]');
    if (add) {
      event.preventDefault();
      this.#openAdd(add);
      return;
    }

    const edit = target.closest?.('[data-uchino-emb-edit]');
    if (edit) {
      event.preventDefault();
      this.#openEdit(edit.closest('[data-uchino-emb-set]'));
      return;
    }

    const remove = target.closest?.('[data-uchino-emb-remove]');
    if (remove) {
      event.preventDefault();
      this.#remove(remove.closest('[data-uchino-emb-set]'));
    }
  };

  #openAdd(button) {
    this.#mode = 'add';
    this.#context = {
      itemKey: button.dataset.itemKey,
      itemVariant: button.dataset.itemVariant,
      itemQuantity: button.dataset.itemQuantity,
      itemProperties: button.dataset.itemProperties || '{}',
      piece: button.dataset.piece || '',
    };

    this.#reset();
    this.#open();
  }

  #openEdit(panel) {
    if (!panel) return;

    this.#mode = 'edit';
    this.#context = {
      embId: panel.dataset.embId,
      feeKey: panel.dataset.feeKey,
      itemKey: panel.dataset.itemKey,
      itemVariant: panel.dataset.itemVariant,
      itemQuantity: panel.dataset.itemQuantity,
      piece: panel.dataset.piece || '',
    };

    this.#reset();

    if (this.nameInput) this.nameInput.value = panel.dataset.name || '';
    this.#check(this.styles, (input) => input.value === panel.dataset.style);
    this.#check(this.threads, (input) => input.value === panel.dataset.thread);

    this.#render();
    this.#open();
  }

  #open() {
    if (this.pieceLabel) this.pieceLabel.textContent = this.#context.piece || '';
    if (this.submitText) {
      this.submitText.textContent = this.#mode === 'edit' ? 'Save changes' : this.addLabel;
    }
    this.dialog?.showModal();
    this.nameInput?.focus();
  }

  close() {
    this.dialog?.close();
  }

  /* --------------------------------------------------------------- render */

  /**
   * The name and the spelling check are cleared; the lettering and the thread
   * are not. Someone putting names on three towels for one family wants the
   * same hand and the same spool each time, and re-picking them for every
   * piece is the tax this design exists to remove.
   */
  #reset() {
    if (this.nameInput) this.nameInput.value = '';
    if (this.confirm) this.confirm.checked = false;
    this.#setError('');
    this.#ensureStyleChecked();
    this.#ensureThreadChecked();
    this.#render();
  }

  #ensureStyleChecked() {
    if (this.styles.some((input) => input.checked)) return;
    if (this.styles[0]) this.styles[0].checked = true;
  }

  /**
   * The default spool sits at position 61 of 63, past the point where a Liquid
   * loop would have found it, so the panel can open with nothing checked. Fall
   * back to the first swatch rather than leaving the shopper a step that looks
   * answered and is not.
   */
  #ensureThreadChecked() {
    if (this.threads.some((input) => input.checked)) return;

    const wanted = this.dataset.defaultThread;
    const match = this.threads.find((input) => input.value.startsWith(`${wanted} `));
    const fallback = match || this.threads[0];
    if (fallback) fallback.checked = true;
  }

  #check(inputs, predicate) {
    let matched = false;
    inputs.forEach((input, index) => {
      input.checked = predicate(input, index);
      if (input.checked) matched = true;
    });
    if (!matched && inputs[0]) inputs[0].checked = true;
  }

  #onChange = () => this.#render();

  get #name() {
    return (this.nameInput?.value || '').replace(/\s+/g, ' ').trim();
  }

  get #style() {
    return this.styles.find((input) => input.checked);
  }

  get #thread() {
    return this.threads.find((input) => input.checked);
  }

  #render() {
    const name = this.#name;
    const style = this.#style;
    const thread = this.#thread;

    if (this.count) this.count.textContent = `${name.length} / ${this.max}`;
    if (this.echo) this.echo.textContent = name || '—';
    if (this.threadName) this.threadName.textContent = thread ? thread.value : '';

    if (this.preview) {
      this.preview.textContent = name;
      this.preview.classList.toggle('is-empty', name.length === 0);
      this.preview.style.fontFamily = style?.dataset.font || '';
      this.preview.style.fontWeight = style?.dataset.weight || '';
      this.preview.style.fontStyle = style?.dataset.italic ? 'italic' : 'normal';
      this.preview.style.color = thread?.dataset.hex || '';
    }

    // Ticking the box before typing a name would confirm a spelling that does
    // not exist yet, so the box follows the field rather than leading it.
    if (this.confirm && !name) this.confirm.checked = false;

    if (this.submit) {
      this.submit.disabled = this.#busy || !name || !style || !thread || !this.confirm?.checked;
    }
  }

  #setError(message) {
    if (!this.error) return;
    this.error.textContent = message;
    this.error.hidden = !message;
  }

  /* ----------------------------------------------------------------- cart */

  #onSubmit = async () => {
    if (this.#busy) return;

    const name = this.#name;
    const style = this.#style;
    const thread = this.#thread;
    if (!name || !style || !thread) return;

    const properties = {
      Name: name,
      Lettering: style.value,
      Thread: thread.value,
      For: this.#context.piece,
      _thread_hex: thread.dataset.hex || '',
    };

    this.#setBusy(true);

    try {
      if (this.#mode === 'edit') {
        await this.#saveEdit(properties);
      } else {
        await this.#saveNew(properties);
      }
      this.close();
      await this.#refreshCart();
    } catch (error) {
      this.#setError(error?.message || 'Something went wrong. Please try again.');
    } finally {
      this.#setBusy(false);
    }
  };

  async #saveNew(properties) {
    const { itemKey, itemVariant, itemQuantity, itemProperties } = this.#context;
    const quantity = Number(itemQuantity) || 1;
    const embId = `e${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;

    /** @type {Record<string, string>} */
    let existing = {};
    try {
      existing = JSON.parse(itemProperties) || {};
    } catch {
      existing = {};
    }

    if (quantity > 1) {
      // Split one piece off the line. The remainder keeps its place in the
      // cart, so the shopper sees two towels where they had three, not a cart
      // rebuilt around them.
      await this.#cart(Theme.routes.cart_change_url, { id: itemKey, quantity: quantity - 1 });
      await this.#cart(Theme.routes.cart_add_url, {
        items: [
          { id: Number(itemVariant), quantity: 1, properties: { ...existing, _emb_id: embId } },
          { id: Number(this.variantId), quantity: 1, properties: { ...properties, _emb_for: embId } },
        ],
      });
      return;
    }

    // A line of one keeps its position: changing its properties in place is the
    // difference between "your towel now has a name on it" and "your towel
    // moved to the bottom of the cart".
    await this.#cart(Theme.routes.cart_change_url, {
      id: itemKey,
      properties: { ...existing, _emb_id: embId },
    });
    await this.#cart(Theme.routes.cart_add_url, {
      items: [{ id: Number(this.variantId), quantity: 1, properties: { ...properties, _emb_for: embId } }],
    });
  }

  async #saveEdit(properties) {
    const { feeKey, embId } = this.#context;
    await this.#cart(Theme.routes.cart_change_url, {
      id: feeKey,
      properties: { ...properties, _emb_for: embId },
    });
  }

  async #remove(panel) {
    if (!panel || this.#busy) return;

    this.#setBusy(true, panel);

    const { feeKey, itemKey, itemVariant, itemQuantity } = panel.dataset;

    try {
      await this.#cart(Theme.routes.cart_change_url, { id: feeKey, quantity: 0 });

      // The piece is removed and re-added rather than having `_emb_id` stripped
      // in place: re-adding merges it back into the plain line of the same
      // towel, where editing the properties would have left two identical
      // lines sitting next to each other.
      await this.#cart(Theme.routes.cart_change_url, { id: itemKey, quantity: 0 });
      await this.#cart(Theme.routes.cart_add_url, {
        items: [{ id: Number(itemVariant), quantity: Number(itemQuantity) || 1 }],
      });

      await this.#refreshCart({ id: String(feeKey), quantity: 0 });
    } catch (error) {
      console.warn('[uchino-embroidery]', error);
    } finally {
      this.#setBusy(false, panel);
    }
  }

  async #cart(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.description || data.message || 'The cart could not be updated.');
    return data;
  }

  /**
   * Hands the refresh back to the theme. Resolving with no sections is what
   * makes cart-items re-render itself from the server, which keeps the rows,
   * the totals and the header count in step without this file knowing any of
   * their section ids.
   */
  async #refreshCart(fallbackLine) {
    // Read first, announce second: the payload is validated, and `update` wants
    // a real cart line id — a variant id or an empty array is rejected, and the
    // rejection is a console warning rather than a thrown error, so it is the
    // kind of mistake that survives a working demo.
    const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    const cart = await response.json();

    const first = cart.items?.[0];
    const deferred = CartLinesUpdateEvent.createPromise();

    // An emptied cart has no line left to point at, and `update` will not take
    // a quantity of zero, so the last thing removed speaks for the change.
    const payload = first
      ? { action: 'update', lines: [{ id: String(first.key), quantity: first.quantity }] }
      : { action: 'remove', lines: [{ id: String(fallbackLine?.id ?? this.variantId), quantity: 1 }] };

    document.dispatchEvent(
      new CartLinesUpdateEvent({ ...payload, context: 'cart', promise: deferred.promise })
    );

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { source: 'uchino-embroidery', items: cart.items, itemCount: cart.item_count },
    });
  }

  #setBusy(busy, panel) {
    this.#busy = busy;
    panel?.classList.toggle('uchino-cemb-busy', busy);
    if (this.submit) this.submit.disabled = busy;
    if (!busy) this.#render();
  }
}

if (!customElements.get('uchino-embroidery')) {
  customElements.define('uchino-embroidery', UchinoEmbroidery);
}
